import { QueryParams } from '../../../types/baseExplorerConfig';

/**
 * Translation of explorer query params into the flat shape the wildfire
 * backend expects.
 *
 * Kept in its own module so the rules can be tested against the real code
 * rather than a copy: the adapter test reimplemented this logic inline to
 * avoid importing `wildfireService`, and the copy drifted -- it passed sort
 * fields straight through, where this maps them to the backend's column
 * names, and it knew nothing of the `response` filter (#771). #839, after the
 * same fix for lightning in #836.
 */

const SORT_FIELD_MAP: Record<string, string> = {
  estimatedSize: 'estimated_size',
  discoverySize: 'discovery_size',
  outSize: 'out_size',
  estimatedStartDate: 'estimated_start_date',
  outDate: 'out_date',
  wildfireStatus: 'wildfire_status',
  fireNumber: 'fire_number',
  number: 'fire_number',
  subCause: 'sub_cause',
};

/**
 * Translate CBE filter/search/sort params into the flat shape the wildfire
 * backend expects. Single source of truth used by both fetchData (view)
 * and buildExportParams (export).
 */
export function translateWildfireParams(params: QueryParams): Record<string, any> {
  const out: Record<string, any> = {};

  if (params?.search?.term) {
    out.search = params.search.term;
  }

  if (params?.sort?.[0]) {
    const field = params.sort[0].field;
    out.sortBy = SORT_FIELD_MAP[field] ?? field;
    out.sortOrder = params.sort[0].direction;
  }

  if (!params?.filters) return out;

  for (const filter of params.filters) {
    switch (filter.field) {
      case 'wildfireStatus':
        out.status = filter.value;
        break;
      case 'response':   // #771
        out.response = filter.value;
        break;
      case 'region':
        out.region = filter.value;
        break;
      case 'cause':
        out.cause = filter.value;
        break;
      case 'estimatedStartDate':
        if (filter.operator === 'gte') out.startDateFrom = filter.value;
        else if (filter.operator === 'lte') out.startDateTo = filter.value;
        break;
      case 'outDate':
        if (filter.operator === 'gte') out.outDateFrom = filter.value;
        else if (filter.operator === 'lte') out.outDateTo = filter.value;
        break;
      case 'number':
        out.number = filter.value;
        break;
      case 'subCause':
        out.subCause = filter.value;
        break;
      case 'objective':
        out.objective = filter.value;
        break;
      case 'estimatedSize':
      case 'discoverySize':
      case 'outSize': {
        const nv = filter.value as { operator: string; value?: number; value2?: number };
        if (!nv?.operator) break;
        const prefix = filter.field;
        if (nv.operator === 'between') {
          if (nv.value != null) out[`${prefix}Min`] = nv.value;
          if (nv.value2 != null) out[`${prefix}Max`] = nv.value2;
        } else if ((nv.operator === 'gte' || nv.operator === 'gt') && nv.value != null) {
          out[`${prefix}Min`] = nv.value;
        } else if ((nv.operator === 'lte' || nv.operator === 'lt') && nv.value != null) {
          out[`${prefix}Max`] = nv.value;
        } else if (nv.operator === 'eq' && nv.value != null) {
          out[`${prefix}Min`] = nv.value;
          out[`${prefix}Max`] = nv.value;
        }
        break;
      }
      default:
        console.warn(`WFE ServiceAdapter: Unsupported filter field: ${filter.field}`);
    }
  }

  return out;
}
